import type { Collection, Data, Extracted, InfaktKind, InfaktMonthDoc, Invoice, InvoiceImportResult, PackNeeds, Rate } from '@biuro/shared';

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function req<T>(method: string, url: string, body?: unknown, form?: FormData): Promise<T> {
  const headers: Record<string, string> = {};
  if (method !== 'GET') headers['x-biuro'] = '1';
  if (body !== undefined) headers['content-type'] = 'application/json';
  const res = await fetch(url, {
    method,
    credentials: 'same-origin',
    headers,
    body: form ?? (body !== undefined ? JSON.stringify(body) : undefined),
  });
  const text = await res.text();
  let j: unknown = null;
  try {
    j = text ? JSON.parse(text) : null;
  } catch {
    j = null;
  }
  if (!res.ok) throw new ApiError(res.status, (j as { error?: string } | null)?.error || 'Coś poszło nie tak. Spróbuj ponownie.');
  return j as T;
}

/** Paczka wstrzymana: do wydruków z Biura brakuje danych (nazwy pozycji, stawki ryczałtu, dane firmy). */
export class PackNeedsError extends ApiError {
  constructor(message: string, readonly needs: PackNeeds) {
    super(409, message);
  }
}

export interface ExtractResponse {
  extracted: Extracted;
  /** Usługa dopasowana po numerze VAT albo nazwie dostawcy. */
  serviceId: string | null;
  /** Kilka pasujących usług – użytkownik wybiera sam. */
  serviceCandidates?: string[];
  message?: string;
}

export interface SyncResult {
  ok: boolean;
  at: string;
  message: string;
  details?: Record<string, number>;
}

export interface Me {
  email: string;
  name?: string | null;
  picture?: string | null;
  owner: string | null;
  isOwner: boolean;
  google: { drive: boolean; calendar: boolean; gmail: boolean };
  driveLink: string | null;
  calendarId: string | null;
  last: { calendar: SyncResult | null; gmail: SyncResult | null };
  infakt: { enabled: boolean; sandbox: boolean };
}

export interface InfaktClient {
  id: number;
  name: string;
  nip: string;
  email: string;
}
export interface InfaktListed {
  number: string;
  date: string;
  uuid: string;
}

export const api = {
  me: () => req<Me>('GET', '/api/me'),
  data: () => req<Data>('GET', '/api/data'),
  put: (col: Collection, id: string, doc: unknown) => req<{ ok: true }>('PUT', `/api/docs/${col}/${encodeURIComponent(id)}`, doc),
  del: (col: Collection, id: string) => req<{ ok: true }>('DELETE', `/api/docs/${col}/${encodeURIComponent(id)}`),
  nbp: (cur: string, date: string) => req<Rate>('GET', `/api/nbp?cur=${encodeURIComponent(cur)}&date=${date}`),
  /** Odczyt danych z PDF faktury: wgranego pliku albo pliku zapisanej faktury z Dysku. */
  extractUpload: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return req<ExtractResponse>('POST', '/api/invoices/extract', undefined, fd);
  },
  extractSaved: (id: string) => req<ExtractResponse>('POST', `/api/invoices/${encodeURIComponent(id)}/extract`, {}),
  upload: (id: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return req<{ ok: true; invoice: Invoice }>('POST', `/api/invoices/${encodeURIComponent(id)}/file`, undefined, fd);
  },
  /** Faktura z pliku jako szkic „do uzupełnienia” (dane z PDF, plik na Dysku). */
  importInvoice: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return req<InvoiceImportResult>('POST', '/api/invoices/import', undefined, fd);
  },
  monthFolder: (ym: string) => req<{ link: string | null }>('GET', `/api/drive/month?ym=${ym}`),
  syncCalendar: () => req<SyncResult>('POST', '/api/calendar/sync', {}),
  syncGmail: () => req<SyncResult>('POST', '/api/gmail/sync', {}),
  disconnect: () => req<{ ok: true }>('POST', '/api/google/disconnect', {}),
  restore: (data: unknown) => req<{ saved: number; rejected: number }>('POST', '/api/restore', data),
  logout: () => req<{ ok: true }>('POST', '/auth/logout', {}),
  infaktClients: (q: string) => req<InfaktClient[]>('GET', `/api/infakt/clients?q=${encodeURIComponent(q)}`),
  infaktFlatRates: () => req<{ symbol: string; label: string }[]>('GET', '/api/infakt/flat-rates'),
  infaktIssue: (b: { templateId: string; ym: string; values: number[]; invoiceDate: string; saleDate: string }) =>
    req<InfaktMonthDoc>('POST', '/api/infakt/issue', b),
  infaktCheck: (ym: string) => req<{ checked: number; found: number; internal: Record<string, InfaktListed[]> }>('POST', '/api/infakt/check', { ym }),
  rodoDrive: () => req<{ link: string }>('POST', '/api/rodo/drive', {}),
  /** Ostatnie dokumenty danego rodzaju w inFakcie, od najnowszego. */
  infaktDocs: (kind: InfaktKind) => req<InfaktListed[]>('GET', `/api/infakt/docs?${new URLSearchParams({ kind })}`),
  /** Surowy JSON dokumentu z inFaktu: wybranego (UUID) albo najnowszego. */
  infaktLatest: (kind: InfaktKind, uuid = '') =>
    req<{ kind: string; data: unknown }>('GET', `/api/infakt/latest?${new URLSearchParams({ kind, ...(uuid ? { uuid } : {}) })}`),
  /** Paczka ZIP dla biura za okres; zwraca plik i podsumowanie z nagłówka. */
  pack: async (from: string, to: string, skipPrint = false): Promise<{ blob: Blob; filename: string; summary: PackSummary }> => {
    const res = await fetch(`/api/pack?from=${from}&to=${to}${skipPrint ? '&skipPrint=1' : ''}`, { credentials: 'same-origin' });
    if (!res.ok) {
      const j = await res.json().catch(() => null) as { error?: string; needs?: PackNeeds } | null;
      if (res.status === 409 && j?.needs) throw new PackNeedsError(j.error || 'Uzupełnij dane do wydruku dokumentów wewnętrznych.', j.needs);
      throw new ApiError(res.status, j?.error || 'Nie udało się przygotować paczki.');
    }
    const cd = res.headers.get('content-disposition') || '';
    const star = cd.match(/filename\*=UTF-8''([^;]+)/i);
    const filename = star ? decodeURIComponent(star[1]) : `Paczka dla biura ${from}.zip`;
    let summary: PackSummary = { invoices: 0, invoiceFiles: 0, internal: 0, printed: 0, missing: 0 };
    try { summary = JSON.parse(res.headers.get('x-pack-summary') || '') as PackSummary; } catch { /* brak nagłówka */ }
    return { blob: await res.blob(), filename, summary };
  },
};

export interface PackSummary {
  invoices: number;
  invoiceFiles: number;
  internal: number;
  /** Wydruki z Biura dokumentów, których PDF-u nie oddał inFakt. */
  printed: number;
  missing: number;
}
